/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, type ReactNode, type RefObject, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import {
    FacetDrilldownProvider,
    FacetMobilePaneProvider,
    useFacetDrilldown,
} from '@/components/category-refinements/drilldown';

/**
 * BG's MOBILE filter control: live BG's full-screen `FILTERS` takeover, opened
 * by the `Filters` segment of the bottom `Filters │ Sort` pill.
 *
 * WHAT THIS REPLACES, AND WHY IT IS A MECHANISM CHANGE RATHER THAN A RESTYLE.
 * Until now ONE element served both the desktop facet rail and the mobile filter
 * panel, and only its WIDTH was gated. So at mobile the "overlay" was the rail —
 * `position: static`, in normal flow, sitting BELOW the site header and the
 * results heading with both still painted and still hit-testable above it.
 * Measured at `clientWidth` 390 before this landed: `#search-filters-panel` at
 * `y = 366.3` on BG, `position: static`, no scroll lock, and an
 * `elementFromPoint` at the header's own coordinates landing in the header.
 *
 * That is the owner's report verbatim — *"the srp mobile filters opened, it
 * covers the whole screen, ours leave the header and search results header
 * visible above the filters overlay"* — and no rule on that element could have
 * fixed it while it remained in flow. A `position: fixed` on the same element
 * was the other option and was rejected: it covers the screen and it cannot
 * supply a focus trap, Escape-to-close, focus restoration or background
 * inertness. The pane is therefore a real dialog.
 *
 * TWO SOURCES AGREE, AND THEY WERE OBTAINED INDEPENDENTLY.
 *
 * (1) DECLARED — live's own shipped stylesheet, already transcribed in
 *     `theme/tokens/brand-bg/search.css` from the mobile dump
 *     `.reference/dumps/bg-search-m-440/mob-search-440.html`:
 *     `.mobile-overlay__pane { position:fixed; inset:0; background:#fff;
 *     z-index:1010 }`, with a 30px centred header at `margin-top:24px` holding
 *     an uppercase h2, and a 30x30 close at the LEADING edge with
 *     `padding-left:16px`. This pane and the SORT pane share that shell.
 *
 * (2) MEASURED — the owner's DevTools capture
 *     `.reference/owner-bg-mobile-2026-08-23/Screenshot 2026-08-23 at 11.59.04 AM.png`,
 *     device toolbar IN FRAME stating `iPhone 12 Pro, 390 x 844, 100%` on
 *     `bergdorfgoodman.com/search/?N=0&Ntt=dress...`, so the width is declared
 *     by the capture rather than inferred from its pixels. Decoded and scanned:
 *
 *       pane        780 x 1688 device px at exactly 2.000 px/CSS
 *                   = exactly 390 x 844 CSS. FULL VIEWPORT — `inset:0`
 *                   confirmed rather than resembled, and agreeing to the pixel
 *                   with the SORT pane measured off a different capture.
 *       header ink  29.5..48.0 CSS below the pane's top
 *       X glyph     19.0 CSS square, left edge 21.5 CSS in, centre 38.8 CSS down
 *       title       centre 1146.0 against a pane centre of 1145.5 — CENTRED to
 *                   within half a device pixel
 *       rows        eight of them, dividers at 522/632/742/852/962/1072/1182/1292,
 *                   EVERY interval exactly 110 device px = 55.0 CSS, zero
 *                   variance. First row top 89.0 CSS below the pane's top.
 *
 * THE GROUPS ARE THE RAIL'S OWN, DELIBERATELY. This pane renders the same
 * `CategoryRefinements` the desktop rail does, because live BG's mobile panel is
 * the same eight groups in the same order — Category, Designer, Size, Color,
 * Price, Availability, Sale, Gender — and that count agrees with the independent
 * desktop finding. Forking a mobile-only list would have created a second source
 * of truth for refinement state.
 *
 * THE ROW RHYTHM ARRIVES AS SCOPED TOKENS, NOT AS NEW RULES. The pane redeclares
 * `--facet-group-header-height`, `--facet-divider`, `--facet-item-font-size` and
 * `--facet-rail-pad-x` ON ITSELF, so the `rail` variant paints live's mobile
 * rhythm here while BG's DESKTOP rail — which mounts `card`, and whose
 * `--facet-*` set carries NM measurements it must not be moved onto — is
 * untouched. Nothing lands in a shared `:root`.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * THE DRILL-DOWN IS BUILT. This block used to say it was not, and both of the
 * reasons it gave have since been retired — deliberately, and by evidence rather
 * than by decision.
 *
 * BLOCKER 1, THE EIGHTH ROW — GONE, AND NOT BY FIXING IT. The previous pass
 * could not ship a drill-down because one group, the injected store-pickup
 * control in `extensions/bopis/components/refine-inventory`, is not a
 * `FilterSection`: it owns private open state and has no group VALUES to put on
 * a second screen. Seven rows that navigate beside one that expands was
 * correctly judged worse than none.
 *
 * That group no longer renders on BG at all. Live BG draws no store-pickup facet
 * under any name on any surface — three independent sources at 6, 7 and 8 groups
 * and not one of them carrying one — so it was `[ours-extra]` here, and
 * `INJECTED_GROUP_SLOT.bg` in `category-refinements/group-order.ts` is now
 * `'none'`. Every row in this pane is a `FilterSection` group by construction.
 * `CategoryRefinements` still checks that slot before drilling and falls back to
 * the accordion if it is ever anything else, so this is a guard rather than an
 * assumption.
 *
 * BLOCKER 2, THE MISSING CAPTURE — GONE, BY GOING AND TAKING IT. That pass wrote
 * that no capture of live's second screen existed on disk and that whoever built
 * this needed one first. Correct, and it was obtainable: live BG answers a
 * headless request carrying the Saks Global infosec user agent, and its mobile
 * takeover opens under 390x844 emulation. Captured 2026-08-23 on
 * `bergdorfgoodman.com/c/womens-clothing-cat441206`, one navigation, measured in
 * the page and persisted to `.reference/live-bg-filter-drilldown/`.
 *
 * WHAT LIVE'S SECOND SCREEN IS. The same pane box — `position: fixed`,
 * `z-index: 1010`, `padding-top: 24px` — with its header geometry UNCHANGED:
 * a 30x30 control at x = 16, y = 24, and a 16px/30.08px uppercase title with
 * `letter-spacing: .16px`, centred (measured centre 194.99 against a pane centre
 * of 195, on both screens). Exactly two things change: the glyph becomes a LEFT
 * chevron, and the title becomes the GROUP'S OWN NAME — `SIZE` where level 1 says
 * `FILTERS`. There is no separate second-level header band and no apply button;
 * the values are simply what the pane now contains.
 *
 * So this pane draws the chrome and `CategoryRefinements` draws both screens'
 * contents — see `FacetDrilldownBody` there for the level-1 row measurements and
 * for what is deliberately NOT generalised from a single group's second screen.
 *
 * THE CHEVRON IS NOW PAINTED, AND ONLY BECAUSE THE NAVIGATION IS REAL. The
 * previous pass withheld it on the grounds that `>` promises navigation and
 * painting it over an in-place accordion would be an invented promise. That
 * reasoning was right and is what makes it safe to paint now: the two were one
 * finding with one cause, and the cause is fixed rather than papered over.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * ACCESSIBILITY COMES FROM `Sheet` — this repo's Radix dialog wrapper, already
 * carrying the focus trap, Escape-to-close, the scroll lock, and `aria-hidden`
 * on everything behind the portal.
 *
 * IT DOES NOT SET `aria-modal`, verified by dumping the rendered attributes,
 * which give `role="dialog"` and `aria-labelledby` and no `aria-modal`. Radix
 * hides the background instead: every body child but the portal gains
 * `aria-hidden="true"`, which removes them from the accessibility tree outright
 * rather than merely advising against them. A test asserting `aria-modal` would
 * redden against a correct implementation; assert the hidden siblings.
 *
 * FOCUS IS RESTORED EXPLICITLY, NOT LEFT TO RADIX. Radix returns focus to
 * whatever held it before the pane opened — which is nothing here. On macOS and
 * on touch, clicking a `<button>` does not focus it, so at the moment of opening
 * `document.activeElement` is the body and there is nothing to restore TO. The
 * trigger's ref is threaded in from the route and focused by hand in
 * `onCloseAutoFocus`.
 *
 * `side="left"` WITH A FULL-WIDTH PANE, rather than a centred `Dialog`, for the
 * reason the sort overlay gives: the measured pane is `inset:0`, which both
 * express; the sheet slides from the leading edge, which is the transition this
 * brand's search takeover already uses; and `Dialog`'s content arm is a centred,
 * radius-bearing, max-width card that would have to be unpicked class by class.
 *
 * BG ONLY BY MOUNT, which is why nothing here is brand-keyed. NM's mobile filter
 * pane is a DIFFERENT BAND — a 75%-wide LEFT panel with the page dimmed beside
 * it, a left-aligned `FILTER BY:` with a colon, a black `DONE` button at the
 * TRAILING edge, an item-count row above the groups and nine groups rather than
 * eight. It lives in `nm-mobile-filter-overlay.tsx`.
 */
export default function BgMobileFilterOverlay({
    open,
    onOpenChange,
    panelId,
    triggerRef,
    children,
}: {
    open: boolean;
    onOpenChange: (next: boolean) => void;
    /**
     * The id the mobile trigger's `aria-controls` points at. It lands on the
     * PANE rather than on the desktop rail, because the pane is what the mobile
     * control actually discloses; the rail is `hidden md:block` and discloses
     * nothing at this width. While the pane is closed the id is absent, which is
     * the standard disclosure shape and which axe permits precisely because the
     * trigger carries `aria-expanded="false"` alongside it.
     */
    panelId: string;
    triggerRef: RefObject<HTMLButtonElement | null>;
    /** The rail's own `CategoryRefinements`, composed at the mount site. */
    children: ReactNode;
}): ReactElement {
    return (
        /* The provider is INSIDE the `Sheet` and outside `SheetContent`'s
           children so that closing the pane unmounts the drill state: a shopper
           who dismisses the takeover from a value screen and reopens it lands on
           the group list, which is what live does. Resetting on close by hand
           would be a second source of truth for the same fact. */
        <FacetMobilePaneProvider>
            <FacetDrilldownProvider>
                <BgMobileFilterOverlayPane
                    open={open}
                    onOpenChange={onOpenChange}
                    panelId={panelId}
                    triggerRef={triggerRef}>
                    {children}
                </BgMobileFilterOverlayPane>
            </FacetDrilldownProvider>
        </FacetMobilePaneProvider>
    );
}

/**
 * The pane itself, split out only so it can consume the context its own wrapper
 * provides — a component cannot read a provider it renders.
 */
function BgMobileFilterOverlayPane({
    open,
    onOpenChange,
    panelId,
    triggerRef,
    children,
}: {
    open: boolean;
    onOpenChange: (next: boolean) => void;
    panelId: string;
    triggerRef: RefObject<HTMLButtonElement | null>;
    children: ReactNode;
}): ReactElement {
    const { t } = useTranslation('search');
    const drilldown = useFacetDrilldown();
    const drilledGroup = drilldown?.group ?? null;
    const backRef = useRef<HTMLButtonElement | null>(null);

    /* The title is the GROUP'S OWN LABEL on the second screen — live titles it
       `SIZE`, not `FILTERS` — uppercased by
       `.bg-srp-filter-overlay-title`'s `text-transform` rather than in JS, so a
       locale whose casing rules differ is the browser's problem and not ours. */
    const title = drilledGroup ? drilledGroup.label : t('filters.bgPanelTitle', { defaultValue: 'Filters' });

    /* FOCUS FOLLOWS THE SCREEN, FOR KEYBOARD USERS ONLY. Drilling in replaces
       every focusable element in the pane, so a keyboard user would otherwise be
       left on a button that no longer exists. The back control is the natural
       landing point and is the one element this component owns; the return trip
       is the list's job, which focuses the row that was left from.

       A POINTER USER MUST NOT BE MOVED. Chromium treats `.focus()` on a
       freshly-mounted element as focus-visible, so doing this unconditionally
       painted a focus ring on the back control after a plain tap — seen on the
       running app before the condition existed. A pointer user who has focused
       nothing should still have focus nowhere; Radix's trap keeps Escape working
       either way, and their first Tab lands on this control because it is the
       pane's first focusable. `openedViaKeyboard` is derived from the row
       click's own `detail`, at the only place that can know. */
    useEffect(() => {
        if (drilledGroup && drilldown?.openedViaKeyboard) {
            backRef.current?.focus();
        }
    }, [drilledGroup, drilldown?.openedViaKeyboard]);

    /* CLOSING RETURNS THE PANE TO ITS LIST. Keyed on `open` rather than wired
       into the close button, so it covers EVERY route out — the X, Escape, a
       scrim click, and a route change that closes the pane — instead of the one
       route somebody remembered.

       It is not free: the state lives above the `Sheet`, because this header
       reads it to choose between a close mark and a back mark, so Radix
       unmounting the pane's content does not discard it. Measured before this
       existed: closing from a value screen and reopening came back to that value
       screen, header still reading `DESIGNER`, with no list in the DOM. */
    const reset = drilldown?.reset;
    useEffect(() => {
        if (!open) {
            reset?.();
        }
    }, [open, reset]);

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="left"
                id={panelId}
                data-testid="bg-mobile-filter-overlay"
                /* `w-full` plus BOTH max-width arms. `SheetContent`'s
                   `side="left"` arm ships `w-3/4 sm:max-w-sm`, so without these
                   the measured full-viewport pane would be a 75% column below
                   640 and a 384px one above it. `max-w-none` ALONE DOES NOT FIX
                   IT: tailwind-merge treats a base utility and its `sm:` variant
                   as different properties, so both survive into the class list
                   and the variant still wins above 640. The `sm:` arm has to be
                   cancelled by its own `sm:` arm.

                   `md:hidden` is the belt-and-braces guard for a resize that
                   happens while the pane is open — the pill that opens it is
                   already `md:hidden`, so this is not the gate, and it is a
                   Tailwind responsive class rather than a JS width branch. */
                className="bg-srp-filter-overlay md:hidden flex w-full max-w-none flex-col gap-0 border-0 p-0 sm:max-w-none [--ui-shadow:none]"
                overlayClassName="bg-srp-filter-overlay-scrim md:hidden"
                /* No description to claim the id Radix would otherwise wire, so
                   the opt-out is explicit rather than shipping a dangling IDREF. */
                aria-describedby={undefined}
                onCloseAutoFocus={(event) => {
                    event.preventDefault();
                    triggerRef.current?.focus();
                }}
                closeButton={null}>
                {/* The X is at the LEADING edge and the title is CENTRED — the
                    opposite arrangement from NM, measured on both rather than
                    assumed from one. Centring is done by taking the title out of
                    the flow rather than by balancing the close with a spacer, so
                    the measured centre holds whatever the title's length. */}
                <div className="bg-srp-filter-overlay-header relative flex shrink-0 items-center">
                    {/* ONE CONTROL IN ONE BOX, swapping its glyph and its job —
                        not two controls with one hidden. Live keeps the same
                        30x30 at x=16, y=24 on both screens, and a single element
                        is the only way that box cannot drift between them. */}
                    {drilledGroup ? (
                        <button
                            type="button"
                            ref={backRef}
                            data-testid="bg-mobile-filter-back"
                            onClick={() => drilldown?.back()}
                            aria-label={t('filters.backToFilters', { defaultValue: 'Back to all filters' })}
                            className="focus-ring-button bg-srp-filter-overlay-close box-content inline-flex cursor-pointer items-center justify-start">
                            <BackGlyph />
                        </button>
                    ) : (
                        <button
                            type="button"
                            data-testid="bg-mobile-filter-close"
                            onClick={() => onOpenChange(false)}
                            aria-label={t('filters.closePanel', { defaultValue: 'Close filters' })}
                            className="focus-ring-button bg-srp-filter-overlay-close box-content inline-flex cursor-pointer items-center justify-start">
                            <CloseGlyph />
                        </button>
                    )}
                    <SheetTitle className="bg-srp-filter-overlay-title pointer-events-none absolute inset-x-0 text-center">
                        {title}
                    </SheetTitle>
                </div>

                <div className="bg-srp-filter-overlay-list min-h-0 flex-1 overflow-y-auto">{children}</div>
            </SheetContent>
        </Sheet>
    );
}

/**
 * THE SECOND SCREEN'S BACK MARK — live's own path, in live's own box.
 *
 * Live serves it as a `background-image` data URI on the same 30x30 control at
 * x = 16, y = 24 that carries the close mark on screen one, so unlike the close
 * glyph beside it the GEOMETRY survives the capture and does not have to be
 * reconstructed from ink. It is a FILLED two-stroke chevron, not a stroked one,
 * spanning x 9..21 and y 4..25.8 — visibly larger than the 7-CSS row chevron,
 * which the screenshot confirms.
 *
 * ONE CHARACTER OF IT IS DERIVED, and the derivation is arithmetic rather than
 * taste. The probe truncated the data URI mid-path, at `…19.91 4 21`; the
 * missing tail is the second stroke's end point, which symmetry fixes exactly.
 * The two strokes meet at y = 14.91, and the captured lower arm ends at
 * (21, 24.726) — 9.816 below the vertex — so the upper arm's end must be 9.816
 * above it: 14.91 − 9.816 = 5.094, i.e. `21 5.09z` at the file's own two-decimal
 * precision. The x is already given by the captured `19.91 4` segment's own
 * +1.09 offset. Nothing here is a preference.
 *
 * Source: `.reference/live-bg-filter-drilldown/`, measured in the page at 390
 * CSS / DPR 2 on `bergdorfgoodman.com/c/womens-clothing-cat441206`, 2026-08-23.
 */
function BackGlyph(): ReactElement {
    return (
        <svg aria-hidden="true" focusable="false" width="30" height="30" viewBox="0 0 30 30" className="size-[30px]">
            <path
                fillRule="evenodd"
                fill="currentColor"
                d="M11.182 14.91 21 24.726l-1.09 1.091L10.09 16 9 14.91 19.91 4 21 5.09z"
            />
        </svg>
    );
}

/**
 * THE FIRST SCREEN'S DISMISS MARK — live's own path, like the back mark above.
 *
 * Both marks come from the same place: live serves them as base64 SVGs on the
 * same 30x30 control at x = 16, y = 24, so the geometry survives and does not
 * have to be reconstructed from ink. The close is FILLED and two-stroke, spanning
 * x 5..24.717 of a 30 box.
 *
 * THE BOX IS 30 AND THE INK IS 19.72. The previous version drew the storefront's
 * generic stroked cross in a 24 box sized to 19px, on the reasoning that live's
 * mark measured "19.0 CSS square". 19 is the INK; applying it as the BOX painted
 * 12.0 CSS here and 10.5 in the sort pane, against live's 19.5 on both — a mark
 * roughly half live's size, and two panes disagreeing where live has one rule.
 * Re-derived from the owner's 390x844 capture by scanning the close region and
 * the title region SEPARATELY; scanned together they merge into a single ink
 * band, which is how the undersized glyph had been reading as a title offset.
 */
function CloseGlyph(): ReactElement {
    return (
        <svg
            aria-hidden="true"
            focusable="false"
            viewBox="0 0 30 30"
            className="size-(--bg-srp-filter-overlay-close-glyph)">
            <g fillRule="evenodd" fill="currentColor">
                <path d="M6.094 5 5 6.094l18.622 18.623 1.095-1.094z" />
                <path d="M23.623 5 5 23.623l1.095 1.094L24.717 6.094z" />
            </g>
        </svg>
    );
}
